import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { hibajegyUzenet } from '../hibajelentes/api.ts';
import { HibajegyHiba, HibajegyStatuszCimke, PrioritasCimke } from '../hibajelentes/Cimkek.tsx';
import Leptetes from '../hibajelentes/Leptetes.tsx';
import { useProfilNevek } from '../hibajelentes/nevek.ts';
import type { HibajegyKep, HibajegySor, HibajegyUzenet } from '../hibajelentes/tipusok.ts';
import { useAktivIroda } from '../iroda/AktivIroda.tsx';
import { formatDatumIdo } from '../lib/formaz.ts';
import { supabase } from '../lib/supabase.ts';
import { Betolto, Gomb, HibaUzenet, Kartya, Mezo, Parbeszed, Ures } from '../ui/elemek.tsx';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const UZENET_MAX = 5000;

function NincsHozzaferes() {
  return (
    <Ures cim="Nincs ilyen hibajelentés, vagy nincs hozzá hozzáférésed." szoveg="Ellenőrizd a linket, vagy térj vissza a listához.">
      <div data-testid="nincs-hozzaferes">
        <Link to="/hibajelentesek" className="font-semibold text-kiemelt underline">Vissza a hibajelentésekhez</Link>
      </div>
    </Ures>
  );
}

/** A jegy képei aláírt URL-lel (a bucket privát). */
function useKepek(hibajegyId: string, lathato: boolean) {
  return useQuery({
    queryKey: ['hibajegyek', 'kepek', hibajegyId],
    enabled: lathato,
    // Az aláírt URL 1 óráig él; a lekérdezés addig friss.
    staleTime: 30 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.from('hibajegy_kepek').select('*').eq('hibajegy_id', hibajegyId).order('created_at');
      if (error) throw error;
      const kepek: HibajegyKep[] = data;
      if (kepek.length === 0) return [];
      const alairt = await supabase.storage.from('hibajegyek').createSignedUrls(kepek.map((k) => k.utvonal), 3600);
      if (alairt.error) throw alairt.error;
      return kepek.map((k) => ({ kep: k, url: alairt.data.find((a) => a.path === k.utvonal)?.signedUrl ?? null }));
    },
  });
}

function Valasz({ hibajegyId }: { hibajegyId: string }) {
  const kliens = useQueryClient();
  const [szoveg, setSzoveg] = useState('');
  const kuldes = useMutation({
    mutationFn: () => hibajegyUzenet(hibajegyId, szoveg),
    onSuccess: async () => {
      setSzoveg('');
      await kliens.invalidateQueries({ queryKey: ['hibajegyek', 'uzenetek', hibajegyId] });
    },
  });
  return (
    <form className="grid gap-3" onSubmit={(e) => { e.preventDefault(); if (szoveg.trim() !== '') kuldes.mutate(); }}>
      <Mezo label="Válasz" multiline maxLength={UZENET_MAX} value={szoveg} onChange={(e) => setSzoveg(e.target.value)} />
      <HibajegyHiba hiba={kuldes.error} />
      <div className="flex justify-end"><Gomb type="submit" toltes={kuldes.isPending} disabled={szoveg.trim() === ''}>Küldés</Gomb></div>
    </form>
  );
}

export default function HibajelentesAdatlap() {
  const { id = '' } = useParams();
  const { jogok, irodak } = useAktivIroda();
  const idErvenyes = UUID.test(id);
  const [nagy, setNagy] = useState<{ url: string; nev: string } | null>(null);

  const jegy = useQuery({
    queryKey: ['hibajegyek', 'jegy', id],
    enabled: idErvenyes,
    queryFn: async () => {
      const { data, error } = await supabase.from('hibajegyek').select('*').eq('id', id).maybeSingle();
      if (error) throw error;
      return data satisfies HibajegySor | null;
    },
  });
  const uzenetek = useQuery({
    queryKey: ['hibajegyek', 'uzenetek', id],
    enabled: idErvenyes && !!jegy.data,
    queryFn: async () => {
      const { data, error } = await supabase.from('hibajegy_uzenetek').select('*').eq('hibajegy_id', id).order('created_at');
      if (error) throw error;
      return data satisfies HibajegyUzenet[];
    },
  });
  const kepek = useKepek(id, idErvenyes && !!jegy.data);
  const nevek = useProfilNevek([jegy.data?.bekuldo_id ?? null, ...(uzenetek.data ?? []).map((u) => u.szerzo_id)]);

  if (!idErvenyes) return <NincsHozzaferes />;
  if (jegy.isPending) return <Betolto />;
  if (jegy.error) return <HibaUzenet hiba={jegy.error} />;
  const j = jegy.data;
  if (!j) return <NincsHozzaferes />;

  const bekuldo = j.bekuldo_id ? nevek.get(j.bekuldo_id) ?? 'Munkatárs' : 'Törölt felhasználó';
  const iroda = irodak.find((i) => i.id === j.office_id)?.nev;

  return (
    <div className="grid min-w-0 grid-cols-1 gap-4">
      <p><Link to="/hibajelentesek" className="inline-flex min-h-10 items-center text-sm text-kiemelt underline">← Vissza a hibajelentésekhez</Link></p>
      <header className="grid gap-2">
        <h1 className="text-2xl font-medium tracking-[-0.025em] wrap-anywhere md:text-3xl">{j.cim}</h1>
        <p className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-szoveg-halk">
          <HibajegyStatuszCimke statusz={j.statusz} />
          <PrioritasCimke prioritas={j.prioritas} />
          <span>{bekuldo}{jogok.super && iroda ? ` · ${iroda}` : ''}</span>
          <span className="szam">{formatDatumIdo(j.created_at)}</span>
        </p>
      </header>

      {jogok.super && <Kartya><Leptetes hibajegyId={j.id} statusz={j.statusz} /></Kartya>}

      <Kartya className="grid gap-3">
        <h2 className="text-lg font-medium tracking-[-0.015em]">Leírás</h2>
        <p className="whitespace-pre-wrap wrap-anywhere">{j.leiras}</p>
        {j.oldal_url && <p className="text-sm break-all text-szoveg-halk">Oldal: {j.oldal_url}</p>}
        {j.lezaras_megjegyzes && (
          <p className="border-l-2 border-vonal-eros pl-3 text-sm whitespace-pre-wrap wrap-anywhere"><span className="text-szoveg-halk">Lezárás megjegyzése: </span>{j.lezaras_megjegyzes}</p>
        )}
      </Kartya>

      {(kepek.data?.length ?? 0) > 0 && (
        <Kartya className="grid gap-3">
          <h2 className="text-lg font-medium tracking-[-0.015em]">Képek</h2>
          <ul aria-label="Csatolt képek" className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {kepek.data!.map(({ kep, url }, i) => (
              <li key={kep.id}>
                {url ? (
                  <button type="button" onClick={() => setNagy({ url, nev: `Csatolt kép ${i + 1}` })} className="block aspect-square w-full overflow-hidden rounded-xl border border-vonal" aria-label={`Kép nagyítása: ${i + 1}`}>
                    <img src={url} alt={`Csatolt kép ${i + 1}`} className="size-full object-cover" />
                  </button>
                ) : <span className="text-sm text-szoveg-halk">A kép nem tölthető be.</span>}
              </li>
            ))}
          </ul>
        </Kartya>
      )}
      <HibaUzenet hiba={kepek.error} />

      <Kartya className="grid gap-4">
        <h2 className="text-lg font-medium tracking-[-0.015em]">Üzenetek</h2>
        <HibaUzenet hiba={uzenetek.error} />
        {uzenetek.isPending && <Betolto />}
        {uzenetek.data && uzenetek.data.length === 0 && <p className="text-sm text-szoveg-halk">Még nincs üzenet.</p>}
        {uzenetek.data && uzenetek.data.length > 0 && (
          <ol aria-label="Üzenetek" className="grid gap-3">
            {uzenetek.data.map((u) => (
              <li key={u.id} data-testid={u.tipus === 'rendszer' ? 'hibajegy-rendszer' : 'hibajegy-uzenet'}
                className={u.tipus === 'rendszer' ? 'text-sm text-szoveg-halk' : 'rounded-xl border border-vonal bg-hatter-melyen p-3'}>
                <p className="mb-1 text-xs text-szoveg-halk">
                  {u.tipus === 'rendszer' ? 'Rendszer' : u.szerzo_id ? nevek.get(u.szerzo_id) ?? 'SocialPro' : 'Törölt felhasználó'}
                  {' · '}<span className="szam">{formatDatumIdo(u.created_at)}</span>
                </p>
                <p className={`whitespace-pre-wrap wrap-anywhere ${u.tipus === 'rendszer' ? 'italic' : ''}`}>{u.szoveg}</p>
              </li>
            ))}
          </ol>
        )}
        <Valasz hibajegyId={j.id} />
      </Kartya>

      <Parbeszed nyitva={nagy !== null} cim="Csatolt kép" onBezar={() => setNagy(null)}>
        {nagy && <img src={nagy.url} alt={nagy.nev} className="max-h-[70dvh] w-full rounded-xl object-contain" />}
        <Gomb valtozat="masodlagos" onClick={() => setNagy(null)}>Bezárás</Gomb>
      </Parbeszed>
    </div>
  );
}
